import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getSharedList,
  createSharedList,
  updateSharedList,
  inviteSharedListMember,
  removeSharedListMember,
  deleteSharedList,
  listSharedLists,
} from '../api/shared-lists';
import type { SharedListPermission } from '@danny-tasks/shared';
import type { SharedListMemberDisplay } from '../api/shared-lists';
import { Copy, Check, Trash2, UserPlus } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

const PERMISSION_LABELS: Record<SharedListPermission, string> = {
  viewer: 'Can view',
  checker: 'Can check off tasks',
  member: 'Can add & edit tasks',
};

interface Props {
  targetType: 'view' | 'project' | 'task';
  targetId: string;
  defaultName: string;
  isOpen: boolean;
  onClose: () => void;
}

export function ShareListModal({
  targetType,
  targetId,
  defaultName,
  isOpen,
  onClose,
}: Props): React.ReactElement {
  const queryClient = useQueryClient();
  const [copied, setCopied] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [invitePermission, setInvitePermission] = useState<SharedListPermission>('viewer');

  const { data: lists = [] } = useQuery({
    queryKey: ['shared-lists'],
    queryFn: listSharedLists,
    enabled: isOpen,
  });
  const existingList = lists.find(
    (l) => l.targetType === targetType && l.targetId === targetId && l.role === 'owner',
  );

  const { data: listDetail } = useQuery({
    queryKey: ['shared-list', existingList?.id],
    queryFn: () => getSharedList(existingList!.id),
    enabled: !!existingList?.id && isOpen,
  });

  const members: SharedListMemberDisplay[] = listDetail?.members ?? [];

  const createList = useMutation({
    mutationFn: () =>
      createSharedList({ name: defaultName, targetType, targetId, linkPermission: null }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['shared-lists'] }),
  });

  const ensureList = async () => {
    if (existingList) return existingList.id;
    const newList = await createSharedList({ name: defaultName, targetType, targetId, linkPermission: null });
    await queryClient.invalidateQueries({ queryKey: ['shared-lists'] });
    return newList.id;
  };

  const updateLink = useMutation({
    mutationFn: async (perm: SharedListPermission | null) => {
      const listId = await ensureList();
      await updateSharedList(listId, { linkPermission: perm });
      return listId;
    },
    onSuccess: (listId) => {
      queryClient.invalidateQueries({ queryKey: ['shared-lists'] });
      queryClient.invalidateQueries({ queryKey: ['shared-list', listId] });
    },
  });

  const stopSharing = useMutation({
    mutationFn: () => deleteSharedList(existingList!.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shared-lists'] });
      onClose();
    },
  });

  const invite = useMutation({
    mutationFn: async () => {
      const listId = await ensureList();
      await inviteSharedListMember(listId, { email: inviteEmail, permission: invitePermission });
      return listId;
    },
    onSuccess: (listId) => {
      setInviteEmail('');
      queryClient.invalidateQueries({ queryKey: ['shared-lists'] });
      queryClient.invalidateQueries({ queryKey: ['shared-list', listId] });
    },
  });

  const removeMember = useMutation({
    mutationFn: (memberId: string) => removeSharedListMember(existingList!.id, memberId),
    onSuccess: () => {
      if (existingList?.id)
        queryClient.invalidateQueries({ queryKey: ['shared-list', existingList.id] });
    },
  });

  const copyLink = () => {
    if (existingList?.shareUrl) {
      navigator.clipboard.writeText(existingList.shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const list = existingList ?? null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Share &ldquo;{defaultName}&rdquo;</DialogTitle>
        </DialogHeader>

        <div className="space-y-6 pt-2">
          {/* Invite — primary action */}
          <div className="space-y-2">
            <Label>Invite someone</Label>
            <div className="flex gap-2">
              <Input
                type="email"
                placeholder="Email address"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                className="flex-1"
              />
              <Select
                value={invitePermission}
                onValueChange={(v) => setInvitePermission(v as SharedListPermission)}
              >
                <SelectTrigger className="w-[170px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="viewer">Can view</SelectItem>
                  <SelectItem value="checker">Can check off tasks</SelectItem>
                  <SelectItem value="member">Can add &amp; edit tasks</SelectItem>
                </SelectContent>
              </Select>
              <Button
                size="icon"
                onClick={() => invite.mutate()}
                disabled={!inviteEmail || invite.isPending}
              >
                <UserPlus className="h-4 w-4" />
              </Button>
            </div>
            {invite.isError && (
              <p className="text-xs text-red-500">
                {(invite.error as Error).message ?? 'Could not send invite'}
              </p>
            )}
          </div>

          {/* Members list */}
          {list && members.length > 0 && (
            <div className="space-y-1.5">
              <Label>Members</Label>
              <div className="divide-y divide-border-subtle-light dark:divide-border-subtle-dark rounded-lg border border-border-subtle-light dark:border-border-subtle-dark overflow-hidden">
                {members.map((m) => (
                  <div key={m.id} className="flex items-center gap-3 px-3 py-2 text-sm">
                    <span className="flex-1 truncate text-text-primary-light dark:text-text-primary-dark">
                      {m.name ?? m.userId}
                    </span>
                    <span className="shrink-0 text-xs text-text-secondary-light dark:text-text-secondary-dark">
                      {PERMISSION_LABELS[m.permission as SharedListPermission] ?? m.role}
                    </span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 shrink-0 text-text-tertiary-light hover:text-red-500 dark:text-text-tertiary-dark"
                      onClick={() => removeMember.mutate(m.id)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Link sharing — secondary */}
          <div className="space-y-2">
            <Label>Link sharing</Label>
            <div className="flex gap-2">
              <Select
                value={list?.linkPermission ?? 'none'}
                onValueChange={(v) => {
                  updateLink.mutate(v === 'none' ? null : (v as SharedListPermission));
                }}
              >
                <SelectTrigger className="flex-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No public link</SelectItem>
                  <SelectItem value="viewer">Anyone can view</SelectItem>
                  <SelectItem value="checker">Anyone can check off</SelectItem>
                  <SelectItem value="member">Anyone can add tasks</SelectItem>
                </SelectContent>
              </Select>
              {list?.linkPermission && (
                <Button variant="outline" size="sm" onClick={copyLink} className="shrink-0">
                  {copied ? (
                    <Check className="h-4 w-4 text-green-500" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                  {copied ? 'Copied' : 'Copy link'}
                </Button>
              )}
            </div>
          </div>

          {/* Primary CTA */}
          {!list ? (
            <Button
              className="w-full"
              onClick={() => createList.mutate()}
              disabled={createList.isPending}
            >
              {createList.isPending ? 'Starting…' : 'Start Sharing'}
            </Button>
          ) : (
            <Button
              variant="ghost"
              className="w-full text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/20"
              onClick={() => stopSharing.mutate()}
              disabled={stopSharing.isPending}
            >
              Stop sharing
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
